import type { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent } from "react";
import { forwardRef } from "react";

import { sendTelemetryEvent } from "@/lib/telemetry";
import type { PropsTelemetryCustom } from "@Components/Modules/Helpers";
import * as stylex from "@stylexjs/stylex";

import type { OmitStyles, QueryXsMax } from "../../styles/tokens.stylex";
import { colors } from "../../styles/tokens.stylex";

const xsMax: QueryXsMax = "@media (max-width: 759px)";

const styles = stylex.create({
    button: {
        display: {
            default: "inline-block",
            [xsMax]: "block",
        },
    },
    link: {
        display: "inline-block",
    },
    shared: {
        backgroundColor: "inherit",
        borderRadius: "1.86em",

        color: "inherit",
        fontSize: "1rem",

        lineHeight: 1.2,
        margin: {
            default: null,
            [xsMax]: "0 auto",
        },
        outline: "none",
        paddingBlock: "0.66em",
        paddingInline: "1.66em",
        position: "relative",
        textAlign: "center",
        transition: "150ms box-shadow ease-in-out",
        transitionProperty: "box-shadow, color, opacity",
    },
});

const variants = stylex.create({
    none: {
        borderRadius: 0,
        padding: 0,
    },

    transparentMagenta: {
        color: colors.b_BrandMagenta,
    },

    transparentDarkBlue: {
        color: colors.bg_BrandPrimary,
    },

    transparentLightBlue: {
        color: colors.fg_Accent,
    },

    magentaBackground: {
        backgroundColor: colors.b_BrandMagenta,

        borderColor: colors.b_BrandMagenta,
        borderStyle: "solid",
        borderWidth: "2px",
        boxShadow: {
            default: `0 0 0 ${colors.b_BrandMagenta}`,
            ":hover": `0 0 0.3em ${colors.b_BrandMagenta}`,
        },
        color: colors.b_Emphasis,
    },

    darkBlueBackground: {
        backgroundColor: colors.bg_BrandPrimary,

        borderColor: colors.bg_BrandPrimary,
        borderStyle: "solid",
        borderWidth: "2px",
        boxShadow: {
            default: `0 0 0 ${colors.bg_BrandPrimary}`,
            ":hover": `0 0 0.3em ${colors.bg_BrandPrimary}`,
        },
        color: colors.b_Emphasis,
    },
    darkBlueMagentaHover: {
        backgroundColor: "transparent",

        borderColor: "none",
        borderStyle: "solid",
        borderWidth: "2px",
        color: {
            default: colors.b_Emphasis,
            ":hover": colors.b_BrandMagenta,
        },
    },

    transparentRoundMagentaBorder: {
        backgroundColor: colors.b_Emphasis,

        borderColor: colors.bg_BrandMagenta,
        borderStyle: "solid",
        borderWidth: "2px",
        boxShadow: {
            default: `0 0 0 ${colors.b_BrandMagenta}`,
            ":hover": `0 0 0.3em ${colors.b_BrandMagenta}`,
        },
        color: colors.b_BrandMagenta,
    },

    transparentRoundGrayBorder: {
        backgroundColor: colors.b_Emphasis,

        borderColor: colors.b_centas,
        borderStyle: "solid",
        borderWidth: "2px",
        boxShadow: {
            default: `0 0 0 ${colors.b_centas}`,
            ":hover": `0 0 0.3em ${colors.b_centas}`,
        },
        color: colors.b_centas,
    },

    disabled: {
        backgroundColor: colors.fg_Disabled,
        color: colors.b_Emphasis,
    },
});

export type IButtonV2 = OmitStyles<ButtonHTMLAttributes<HTMLButtonElement>> & {
    variant?: keyof typeof variants;
};

export const ButtonV2 = forwardRef<HTMLButtonElement, IButtonV2>(
    ({ onClick, variant, type = "button", styleX, ...props }, ref) => {
        const onSubmitClick =
            type !== "submit"
                ? (ev: MouseEvent<HTMLButtonElement>) => {
                      ev.preventDefault();
                      ev.stopPropagation();
                      if (onClick) {
                          onClick(ev);
                      }
                  }
                : onClick;

        return (
            <button
                {...props}
                type={type}
                ref={ref}
                onClick={onSubmitClick}
                {...stylex.props(styles.shared, styles.button, variant && variants[variant], styleX)}
            />
        );
    },
);

export type ILinkV2 = PropsTelemetryCustom &
    OmitStyles<AnchorHTMLAttributes<HTMLAnchorElement>> & {
        variant?: keyof typeof variants;
        dataTestId?: string;
    };

export const LinkV2 = forwardRef<HTMLAnchorElement, ILinkV2>(
    ({ telemetryCustom, variant, styleX, onClick, dataTestId, ...props }, ref) => {
        const onLinkClick: React.MouseEventHandler<HTMLAnchorElement> = (event) => {
            sendTelemetryEvent(telemetryCustom);

            onClick?.(event);

            return undefined;
        };

        return (
            <a
                {...props}
                ref={ref}
                {...stylex.props(styles.shared, styles.link, variant && variants[variant], styleX)}
                onClick={onLinkClick}
                data-testid={dataTestId}
            />
        );
    },
);
